import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Slider } from "./ui/slider";
import { toast } from "sonner";
import { 
  AlertTriangle, ArrowRight, Zap, Calendar, 
  CreditCard, TrendingUp, X, Loader2 
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const QuotaExhaustedModal = ({ 
  isOpen, 
  onClose, 
  quotaStatus, 
  API, 
  onQuotaUpdated 
}) => {
  const navigate = useNavigate();
  const [borrowAmount, setBorrowAmount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [selectedCredits, setSelectedCredits] = useState(1);

  if (!isOpen || !quotaStatus) return null;

  const plan = quotaStatus.plan || "trial";
  const isTrialOrFree = ["trial", "free"].includes(plan);
  const maxBorrow = quotaStatus.next_month_available || 0;
  
  // Cost per quote based on plan
  const costPerQuote = {
    starter: 1.10,
    professional: 0.90,
    business: 0.70
  }[plan] || 1.00;

  const handleUpgrade = () => {
    onClose();
    navigate("/pricing");
  };

  const handleBorrowWithBilling = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/quota/borrow-with-billing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          quotes_to_borrow: borrowAmount,
          success_url: `${window.location.origin}/dashboard?borrowed=${borrowAmount}`,
          cancel_url: `${window.location.origin}/dashboard`
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to setup payment");
      }
    } catch (error) {
      toast.error("Payment setup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBuyPAYG = async (credits = 1) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/quota/buy-payg`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          credits,
          success_url: `${window.location.origin}/dashboard?payg=${credits}`,
          cancel_url: `${window.location.origin}/dashboard`
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to setup payment");
      }
    } catch (error) {
      toast.error("Payment setup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg bg-card border-white/10 shadow-2xl">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-muted-foreground hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-500/20 rounded-full">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <CardTitle>Quote Limit Reached</CardTitle>
              <CardDescription>
                You've used all {quotaStatus.quota_limit} quotes this month
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current Status */}
          <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
            <span className="text-sm text-muted-foreground">Current Plan</span>
            <Badge variant="secondary" className="capitalize">{plan}</Badge>
          </div>

          {isTrialOrFree ? (
            /* Trial/Free Users - Only show upgrade option */
            <div className="space-y-4">
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Upgrade to Keep Quoting
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Unlock more quotes and powerful features with a paid plan.
                </p>
                <ul className="text-sm space-y-2 mb-4">
                  <li className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    Starter: 25 quotes/month - £59
                  </li>
                  <li className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    Professional: 75 quotes/month - £99
                  </li>
                  <li className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    Business: 150 quotes/month - £150
                  </li>
                </ul>
                <Button 
                  onClick={handleUpgrade} 
                  className="w-full"
                  data-testid="upgrade-btn"
                >
                  View Plans & Upgrade
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Or buy individual quotes at £9 each
                </p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      disabled={loading}
                      className="mt-2"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Buy Credits
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-48">
                    <DropdownMenuItem onClick={() => handleBuyPAYG(1)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      1 Credit - £9
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBuyPAYG(3)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      3 Credits - £27
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBuyPAYG(5)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      5 Credits - £45
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBuyPAYG(10)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      10 Credits - £90
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ) : (
            /* Paid Users - Show upgrade OR borrow options */
            <div className="space-y-4">
              {/* Option 1: Upgrade */}
              <div className="p-4 border border-white/10 rounded-lg hover:border-primary/50 transition-colors">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Upgrade Your Plan
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Get more quotes each month with a higher tier plan.
                </p>
                <Button 
                  variant="outline" 
                  onClick={handleUpgrade}
                  className="w-full"
                  data-testid="upgrade-btn"
                >
                  View Upgrade Options
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Option 2: Borrow from next month */}
              {maxBorrow > 0 && (
                <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Borrow from Next Month
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Use some of next month's quota now. Billed upfront at your plan rate.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Quotes to borrow</span>
                      <span className="font-bold text-primary">{borrowAmount}</span>
                    </div>
                    <Slider
                      value={[borrowAmount]}
                      onValueChange={(v) => setBorrowAmount(v[0])}
                      min={1}
                      max={Math.min(maxBorrow, 25)}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>1 quote</span>
                      <span>{Math.min(maxBorrow, 25)} max</span>
                    </div>
                    
                    <div className="p-3 bg-background/50 rounded-lg">
                      <div className="flex justify-between text-sm">
                        <span>Cost ({borrowAmount} × £{costPerQuote.toFixed(2)})</span>
                        <span className="font-bold">£{(borrowAmount * costPerQuote).toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Deducted from next month's allowance
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handleBorrowWithBilling}
                      disabled={loading}
                      className="w-full"
                      data-testid="borrow-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Pay & Borrow {borrowAmount} Quotes
                    </Button>
                  </div>
                </div>
              )}

              {/* Option 3: Buy PAYG */}
              <div className="text-center pt-2">
                <p className="text-sm text-muted-foreground mb-2">
                  Or buy individual quotes at £9 each
                </p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      disabled={loading}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Buy PAYG Credits
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-48">
                    <DropdownMenuItem onClick={() => handleBuyPAYG(1)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      1 Credit - £9
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBuyPAYG(3)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      3 Credits - £27
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBuyPAYG(5)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      5 Credits - £45
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBuyPAYG(10)}>
                      <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                      10 Credits - £90
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuotaExhaustedModal;
